import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Drawer, Input, Pagination, Result, Select, Spin, Tooltip } from "antd";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { getCustomEmail, getCustomEmailRecipients, getCustomEmailRecipient } from "../../Services/customEmail";
import { CePage } from "./styles";
import { renderTemplate, iframeDoc, SUB_LABEL, TYPE_LABEL, fmtDay, fmtTime } from "./logic";

const statusTag = (sent) => (sent ? <span className="tag new">Sent</span> : <span className="tag bad">Not sent</span>);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const typeLabel = (v) => (!v?.type ? "Default" : v.type === "url" ? SUB_LABEL[v.url_sub_type] : TYPE_LABEL[v.type]);

// {itinerary_N} in a stored link text -> the city this user got from the Nth itinerary_download variable.
const withCities = (text, variables, itineraries) => {
  const keys = variables.filter((v) => v.type === "url" && v.url_sub_type === "itinerary_download").map((v) => v.key);
  return (text || "").replace(/\{itinerary_(\d+)\}/g, (m, n) => itineraries.find((i) => i.variable_key === keys[n - 1])?.label || m);
};

const RecipientDrawer = ({ campaign, recipientId, onClose }) => {
  const [openLink, setOpenLink] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["custom_email_recipient", campaign.id, recipientId],
    queryFn: () => getCustomEmailRecipient(campaign.id, recipientId),
    enabled: !!recipientId,
    refetchOnWindowFocus: false,
    retry: false,
  });
  useEffect(() => setOpenLink(null), [recipientId]);
  const html = useMemo(() => (data ? renderTemplate(data.template.body, data.email_data, data.recipient.email) : ""), [data]);
  const subject = useMemo(() => (data ? renderTemplate(data.template.subject, data.email_data, data.recipient.email).replace(/<[^>]+>/g, "") : ""), [data]);
  const r = data?.recipient;

  return (
    <Drawer open={!!recipientId} onClose={onClose} width={Math.min(760, window.innerWidth)} closable={false} destroyOnClose
      styles={{ body: { background: "#f7f8fb", padding: "18px 20px" } }}>
      <CePage>
        {isLoading || !data ? <Spin /> : (
          <>
            <div className="head">
              <div>
                <div className="crumb">{campaign.title}</div>
                <h2>{r.name}</h2>
                <div className="note">#{r.user_id} · {r.email} · {statusTag(r.is_sent)}</div>
              </div>
              <Button onClick={onClose}>Close</Button>
            </div>

            <div className="sec"><h3>Itineraries in this email</h3></div>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Itinerary</th><th className="n">Downloads</th></tr></thead>
                <tbody>
                  {data.itineraries.length ? data.itineraries.map((i) => (
                    <tr key={i.variable_key}>
                      <td><b>{i.label}</b> <span className="muted">· {i.user_itinerary_id ? `#${i.user_itinerary_id}` : "deleted"} {i.headline || ""}</span></td>
                      <td className="n">{i.download_count}</td>
                    </tr>
                  )) : <tr><td colSpan={2} className="empty">No itinerary links in this email.</td></tr>}
                </tbody>
              </table>
            </div>

            {r.is_sent ? (
              <>
                <div className="sec"><h3>Clicks by this user</h3></div>
                <div className="tablewrap">
                  <table>
                    <thead><tr><th>Link</th><th>Type</th><th className="n">Clicks</th><th>Last click</th></tr></thead>
                    <tbody>
                      {data.links.map((l) => {
                        const n = l.clicks.length;
                        const isOpen = openLink === l.id;
                        return (
                          <Fragment key={l.id}>
                            <tr className={`cliktop ${n ? "has" : ""}`} onClick={n ? () => setOpenLink(isOpen ? null : l.id) : undefined}
                              aria-expanded={n ? isOpen : undefined}>
                              <td>{n ? <span className="disc">{isOpen ? "▾" : "▸"}</span> : null} {withCities(l.link_text, data.variables, data.itineraries)}</td>
                              <td><span className="tag">{SUB_LABEL[l.link_type] || l.link_type}</span></td>
                              <td className="n">{n}</td>
                              <td className="muted">{n ? fmtTime(l.clicks[n - 1].clicked_at) : "–"}</td>
                            </tr>
                            {isOpen && (
                              <tr className="clikdetail">
                                <td colSpan={4}>
                                  <div className="tablewrap" style={{ margin: "6px 0 4px" }}>
                                    <table>
                                      <thead><tr><th>Clicked at</th><th>IP address</th><th>Browser / OS</th><th>Device</th></tr></thead>
                                      <tbody>
                                        {l.clicks.map((k, i) => {
                                          const ci = k.client_info || {};
                                          return (
                                            <tr key={i}>
                                              <td className="mono" style={{ fontSize: 11.5 }}>{fmtTime(k.clicked_at)}</td>
                                              <td className="mono" style={{ fontSize: 11.5 }}>{k.ip_address || <span className="muted">Unknown</span>}</td>
                                              <td>{ci.browser ? `${ci.browser} · ${ci.os || "?"}` : <span className="muted">No user-agent (bot / blocked)</span>}</td>
                                              <td className="muted">{[ci.device_type, ci.platform].filter(Boolean).join(" · ") || "–"}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="note" style={{ marginTop: 6 }}>Click a row with clicks to see the IP address, browser and device for each one.</div>
              </>
            ) : (
              <div className="blocked" style={{ marginTop: 12 }}>
                Not sent: {r.failure_reason || "unknown reason"}. The values below were prepared, but this user never received the email, so there are no clicks.
              </div>
            )}

            <div className="sec"><h3>Variables and values {r.is_sent ? "sent" : "prepared"}</h3></div>
            <div className="panel kv">
              <div className="h">Variable</div><div className="h">Type</div><div className="h">Value</div>
              {data.variables.map((v) => {
                const val = data.email_data[v.key];
                return (
                  <Fragment key={v.key}>
                    <div>{v.key}</div>
                    <div><span className="tag">{typeLabel(v)}</span></div>
                    <div className="mono" style={{ fontSize: 11.5 }}>
                      {val === undefined ? "(left out: template default or hidden block)" : v.type === "html" ? "HTML content, see the email below" : String(val)}
                    </div>
                  </Fragment>
                );
              })}
            </div>

            <div className="sec"><h3>{r.is_sent ? "Email as received" : "Email that was prepared"}</h3><span className="note">Template saved at send time + this user's values</span></div>
            <div className="panel">
              <div className="pvmeta"><span className="muted">Subject:</span> {subject || "(no subject)"}</div>
              <div className="mailcanvas"><iframe title="Email as sent" sandbox="" srcDoc={iframeDoc(html)} /></div>
            </div>
          </>
        )}
      </CePage>
    </Drawer>
  );
};

const CustomEmailDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [openRecipient, setOpenRecipient] = useState(null);

  const applySearch = useMemo(() => debounce(setSearch, 500), []);
  useEffect(() => () => applySearch.cancel(), [applySearch]);
  useEffect(() => { setPage(1); }, [search, status]);

  const detail = useQuery({ queryKey: ["custom_email", id], queryFn: () => getCustomEmail(id), refetchOnWindowFocus: false, retry: false });
  const recips = useQuery({
    queryKey: ["custom_email_recipients", id, page, pageSize, search, status],
    queryFn: () => getCustomEmailRecipients(id, { skip: (page - 1) * pageSize, limit: pageSize, search, status }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const firstSent = useQuery({
    queryKey: ["custom_email_recipients", id, "first_sent"],
    queryFn: () => getCustomEmailRecipients(id, { skip: 0, limit: 1, status: "sent" }),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const back = () => navigate("/email_campaign?tab=custom");
  if (detail.isError) return <Result status="404" title="Campaign not found" extra={<Button onClick={back}>Back to Custom Email</Button>} />;
  if (detail.isLoading) return <Spin style={{ margin: 40 }} />;
  const { campaign: c, variables, links } = detail.data;
  const itinCount = variables.filter((v) => v.type === "url" && v.url_sub_type === "itinerary_download").length;
  const max = Math.max(0, ...links.map((l) => l.click_count));
  const rows = recips.data?.recipients || [];
  const sample = firstSent.data?.recipients?.[0];

  return (
    <CePage>
      <div className="crumb">
        <Tooltip title="Go back">
          <button type="button" className="backbtn" aria-label="Go back" onClick={back}><ArrowLeftOutlined /></button>
        </Tooltip>
        <Tooltip title="Go back to Email Campaigns">
          <button type="button" className="linkbtn" onClick={back}>Email Campaigns</button>
        </Tooltip> / <Tooltip title="Go back to Custom Email list">
          <button type="button" className="linkbtn" onClick={back}>Custom Email</button>
        </Tooltip> / #{c.id}
      </div>
      <div className="head">
        <div>
          <h2>{c.title}</h2>
          <div className="note">Template: {c.template_name || c.template_id} · sent {fmtDay(c.created_at)}{itinCount ? ` · ${plural(itinCount, "itinerary download link")}` : ""}</div>
        </div>
        <div className="row">
          <Button disabled={!sample} onClick={() => setOpenRecipient(sample.id)}>View email as sent</Button>
          <Button loading={detail.isFetching || recips.isFetching} onClick={() => { detail.refetch(); recips.refetch(); }}>Refresh</Button>
        </div>
      </div>

      <div className="stats">
        <div className="stat"><span>Recipients</span><b>{c.total_recipients}</b></div>
        <div className="stat"><span>Sent</span><b>{c.sent_count}</b>{c.sent_count < c.total_recipients && <div className="note">{c.total_recipients - c.sent_count} not sent</div>}</div>
        <div className="stat"><span>Total clicks</span><b>{c.total_click_count}</b></div>
        <div className="stat"><span>Useful</span><b style={{ color: "var(--good)" }}>{c.useful_count}</b></div>
        <div className="stat"><span>Not Useful</span><b style={{ color: "var(--bad)" }}>{c.not_useful_count}</b></div>
      </div>

      <div className="sec">
        <h3>Clicks per link</h3>
        {/* only when a link name has an {itinerary_N} placeholder in it */}
        {links.some((l) => /\{itinerary_\d+\}/.test(l.link_text || "")) && (
          <span className="note">In a link name, {"{itinerary_1}"} stands for each recipient's own itinerary (a different city per user)</span>
        )}
      </div>
      <div className="tablewrap">
        <table>
          <thead><tr><th>Link</th><th>Type</th><th className="n">Clicks</th><th style={{ width: "40%" }} /></tr></thead>
          <tbody>
            {links.length ? links.map((l) => (
              <tr key={l.id}>
                <td>{l.link_text}</td>
                <td><span className="tag">{SUB_LABEL[l.link_type] || l.link_type}</span></td>
                <td className="n">{l.click_count}</td>
                <td><span className="bar" style={{ width: `${max ? Math.round((l.click_count / max) * 100) : 0}%` }} /></td>
              </tr>
            )) : <tr><td colSpan={4} className="empty">No tracked links in this email.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="sec">
        <h3>Recipients</h3>
        <div className="row">
          <Input aria-label="Search name or email" allowClear prefix={<SearchOutlined style={{ color: "#8a90a8" }} />} placeholder="Search name or email" style={{ width: 220 }}
            value={typed} onChange={(e) => { setTyped(e.target.value); applySearch(e.target.value.trim()); }} />
          <Select aria-label="Send status" style={{ width: 120 }} value={status} onChange={setStatus}
            options={[{ value: "", label: "All" }, { value: "sent", label: "Sent" }, { value: "not_sent", label: "Not sent" }]} />
        </div>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>User</th><th>Email</th><th>Itineraries in this email</th><th>Status</th><th className="n">Clicks</th><th className="n">Useful</th><th className="n">Not Useful</th><th /></tr>
          </thead>
          <tbody>
            {recips.isLoading ? <tr><td colSpan={8} className="empty"><Spin /></td></tr> : rows.length ? rows.map((r) => (
              <tr key={r.id}>
                <td>#{r.user_id} {r.name}</td>
                <td className="muted">{r.email}</td>
                <td>
                  {r.itineraries.length ? r.itineraries.map((i, k) => (
                    <div className="note" key={i.variable_key}>
                      {r.itineraries.length > 1 ? `${k + 1}: ` : ""}<b style={{ color: "var(--text)" }}>{i.label}</b>
                      {i.user_itinerary_id ? ` · #${i.user_itinerary_id}` : ""} · {plural(i.download_count, "download")}
                    </div>
                  )) : <span className="muted">–</span>}
                </td>
                <td>{statusTag(r.is_sent)} {!r.is_sent && <span className="note">{r.failure_reason}</span>}</td>
                <td className="n">{r.total_click_count}</td>
                <td className="n">{r.useful_count}</td>
                <td className="n">{r.not_useful_count}</td>
                <td><Button size="small" onClick={() => setOpenRecipient(r.id)}>{r.is_sent ? "View email" : "View details"}</Button></td>
              </tr>
            )) : <tr><td colSpan={8} className="empty">No recipients match.</td></tr>}
          </tbody>
        </table>
        {(recips.data?.total_count || 0) > 0 && (
          <div className="pager">
            <Pagination size="small" current={page} pageSize={pageSize} total={recips.data.total_count} showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]} showTotal={(t, rg) => `${rg[0]}–${rg[1]} of ${t}`}
              onChange={(p, s) => { setPage(s !== pageSize ? 1 : p); setPageSize(s); }} />
          </div>
        )}
      </div>
      <p className="note" style={{ marginTop: 8 }}>One row per user = one email, even with several itineraries.</p>

      <RecipientDrawer campaign={c} recipientId={openRecipient} onClose={() => setOpenRecipient(null)} />
    </CePage>
  );
};

export default CustomEmailDetail;
